import { useState, useEffect } from 'react';
import Editable from './Editable';
import type { Lang, Translations, SeoData } from '../types';
import { assets } from '../assetsConfig';

interface NavbarProps {
  lang: Lang;
  onLangChange: (lang: Lang) => void;
  t: Translations;
  seo: SeoData;
  isEditable: boolean;
  onUpdate: (key: string, val: string) => void;
}

export default function Navbar({ lang, onLangChange, t, seo, isEditable, onUpdate }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY >= 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed w-full top-0 left-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-[#FAF8F5] shadow-md py-0' : 'bg-[#FAF8F5]/40 backdrop-blur-sm py-2'}`}>
      <nav className="container mx-auto px-4 flex items-center justify-between h-20 lg:h-24">
        <a href="#" className="h-14 lg:h-20 w-auto transition-transform duration-300 hover:scale-105">
          <img src={assets.img.logo} alt={seo.logoAlt} className="h-full w-full object-contain" />
        </a>

        <ul className="hidden lg:flex items-center gap-8">
          <li><a href="#hero" className="nav-link text-[15px]"><Editable isEditable={isEditable} value={t.nav_home} onChange={(v) => onUpdate('nav_home', v)} as="span" /></a></li>
          <li><a href="#ueber-uns" className="nav-link text-[15px]"><Editable isEditable={isEditable} value={t.nav_about} onChange={(v) => onUpdate('nav_about', v)} as="span" /></a></li>
          <li><a href="#leistungen" className="nav-link text-[15px]"><Editable isEditable={isEditable} value={t.nav_services} onChange={(v) => onUpdate('nav_services', v)} as="span" /></a></li>
          <li><a href="#kontakt" className="nav-link text-[15px]"><Editable isEditable={isEditable} value={t.nav_contact} onChange={(v) => onUpdate('nav_contact', v)} as="span" /></a></li>
        </ul>

        <div className="hidden lg:flex items-center gap-6">
          <div className="flex items-center gap-4 border-r border-slate-300 pr-6">
            <a href={t.social_fb_link} onClick={(e) => isEditable && e.preventDefault()} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-2xl text-slate-400 hover:text-[#1877F2] transition-colors duration-300 cursor-pointer"><i className="ri-facebook-circle-fill" aria-hidden="true"></i></a>
            <a href={t.social_messenger_link} onClick={(e) => isEditable && e.preventDefault()} target="_blank" rel="noopener noreferrer" aria-label="Messenger" className="text-2xl text-slate-400 hover:text-[#00B2FF] transition-colors duration-300 cursor-pointer"><i className="ri-messenger-fill" aria-hidden="true"></i></a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => onLangChange('de')} className={`cursor-pointer overflow-hidden rounded-md transition-all duration-300 hover:scale-110 ${lang === 'de' ? 'ring-2 ring-care' : ''}`}><img src="https://flagcdn.com/w40/de.png" alt={seo.flagDeAlt} className="w-8 h-auto block" /></button>
            <button onClick={() => onLangChange('pl')} className={`cursor-pointer overflow-hidden rounded-md transition-all duration-300 hover:scale-110 ${lang === 'pl' ? 'ring-2 ring-care' : ''}`}><img src="https://flagcdn.com/w40/pl.png" alt={seo.flagPlAlt} className="w-8 h-auto block" /></button>
          </div>
        </div>

        <button type="button" onClick={() => setIsMobileMenuOpen(true)} aria-label="Menü öffnen" aria-expanded={isMobileMenuOpen} className="lg:hidden text-3xl text-prestige cursor-pointer">
          <i className="ri-menu-3-line" aria-hidden="true"></i>
        </button>
      </nav>

      <div className={`fixed top-0 ${isMobileMenuOpen ? 'left-0' : 'left-[-100%]'} w-full h-screen bg-[#FAF8F5] z-50 flex flex-col items-center justify-center transition-all duration-300 lg:hidden`}>
        <button aria-label="Menü schließen" onClick={() => setIsMobileMenuOpen(false)} className="absolute top-6 right-6 text-4xl text-prestige cursor-pointer">
          <i className="ri-close-line"></i>
        </button>
        <ul className="flex flex-col items-center gap-8 text-2xl font-bold text-prestige w-full px-8 text-center">
          <li className="w-full">
            <a href="#hero" onClick={(e) => { if (isEditable) e.preventDefault(); else setIsMobileMenuOpen(false); }} className="block w-full">
              <Editable isEditable={isEditable} value={t.nav_home} onChange={(v) => onUpdate('nav_home', v)} as="span" className="w-full text-center" />
            </a>
          </li>
          <li className="w-full">
            <a href="#ueber-uns" onClick={(e) => { if (isEditable) e.preventDefault(); else setIsMobileMenuOpen(false); }} className="block w-full">
              <Editable isEditable={isEditable} value={t.nav_about} onChange={(v) => onUpdate('nav_about', v)} as="span" className="w-full text-center" />
            </a>
          </li>
          <li className="w-full">
            <a href="#leistungen" onClick={(e) => { if (isEditable) e.preventDefault(); else setIsMobileMenuOpen(false); }} className="block w-full">
              <Editable isEditable={isEditable} value={t.nav_services} onChange={(v) => onUpdate('nav_services', v)} as="span" className="w-full text-center" />
            </a>
          </li>
          <li className="w-full">
            <a href="#kontakt" onClick={(e) => { if (isEditable) e.preventDefault(); else setIsMobileMenuOpen(false); }} className="block w-full">
              <Editable isEditable={isEditable} value={t.nav_contact} onChange={(v) => onUpdate('nav_contact', v)} as="span" className="w-full text-center" />
            </a>
          </li>
        </ul>
        <div className="flex items-center gap-8 mt-12">
            <button onClick={() => {onLangChange('de'); setIsMobileMenuOpen(false);}} className="cursor-pointer"><img src="https://flagcdn.com/w40/de.png" alt={seo.flagDeAlt} className="w-10 h-auto rounded-sm" /></button>
            <button onClick={() => {onLangChange('pl'); setIsMobileMenuOpen(false);}} className="cursor-pointer"><img src="https://flagcdn.com/w40/pl.png" alt={seo.flagPlAlt} className="w-10 h-auto rounded-sm" /></button>
        </div>
      </div>
    </header>
  );
}